/**
 * SCD R3 — Moteur des améliorations par magasin (arbre, chantiers, effets).
 * État porté par chaque magasin : ameliorations {id: niveau}, chantiers [{amelioId, niveauCible, debutJour, finJour, fermeturePartielle}].
 */
import data from '../data/dgs/ameliorations_magasin.json';

export const CATEGORIES_AMELIORATIONS = data.categories;
export const AMELIORATIONS = data.ameliorations;
export const CHANTIER_TRAFIC_MULT = 1 - (data.chantier_trafic_malus_pct || 30) / 100;

export const getAmelioration = (id) => AMELIORATIONS.find(a => a.id === id) || null;
export const coefType = (typeId) => data.coef_type[typeId] || 1;
export const coutNiveau = (niveau, typeId) => Math.round(niveau.cout * coefType(typeId));
export const entretienNiveau = (niveau, typeId) => Math.round((niveau.entretien_mensuel || 0) * coefType(typeId));

export const chantiersEnCours = (mag, totalDays) =>
  (mag?.chantiers || []).filter(c => c.finJour > totalDays);

export const fermeturePartielle = (mag, totalDays) =>
  chantiersEnCours(mag, totalDays).some(c => c.fermeturePartielle);

/** L'achat du prochain niveau est-il possible ? { ok, reason, niveau, cout, dep, depNiveau } */
export const peutAcheter = ({ amelio, magasin, secteurId, treasury, totalDays }) => {
  if (!amelio || !magasin) return { ok: false, reason: 'inconnu' };
  if (amelio.types && !amelio.types.includes(magasin.typeId)) return { ok: false, reason: 'type' };
  if (amelio.secteurs && !amelio.secteurs.includes(secteurId)) return { ok: false, reason: 'secteur' };
  if ((magasin.chantiers || []).some(c => c.amelioId === amelio.id && c.finJour > totalDays)) {
    return { ok: false, reason: 'chantier' };
  }
  const etat = magasin.ameliorations || {};
  const niveau = amelio.niveaux.find(n => n.niveau === (etat[amelio.id] || 0) + 1);
  if (!niveau) return { ok: false, reason: 'max' };
  for (const [depId, min] of Object.entries(amelio.dependances || {})) {
    if ((etat[depId] || 0) < min) {
      return { ok: false, reason: 'dependance', dep: getAmelioration(depId), depNiveau: min, niveau };
    }
  }
  const cout = coutNiveau(niveau, magasin.typeId);
  if ((treasury || 0) < cout) return { ok: false, reason: 'fonds', cout, niveau };
  return { ok: true, niveau, cout };
};

/** Agrège les effets des améliorations acquises d'UN magasin. */
export const computeAmelFx = (mag) => {
  const fx = {
    traficPct: 0, conversionPct: 0, panierPct: 0,
    satisfactionJour: 0, capaciteCaisse: 0, capaciteReservePct: 0,
    partCcPct: 0, entretienJour: 0,
  };
  const etat = mag?.ameliorations || {};
  AMELIORATIONS.forEach(a => {
    const niv = etat[a.id] || 0;
    if (niv <= 0) return;
    a.niveaux.forEach(n => {
      if (n.niveau > niv) return;
      fx.entretienJour += entretienNiveau(n, mag.typeId) / 30;
      const e = n.effets || {};
      fx.traficPct += e.trafic_pct || 0;
      fx.conversionPct += e.conversion_pct || 0;
      fx.panierPct += e.panier_pct || 0;
      fx.satisfactionJour += (e.satisfaction_pts || 0) * 0.02;
      fx.capaciteCaisse += e.capacite_caisse || 0;
      fx.capaciteReservePct += e.capacite_reserve_pct || 0;
      fx.partCcPct = Math.max(fx.partCcPct, e.part_cc_pct || 0);
    });
  });
  return fx;
};

/** Applique les chantiers terminés au jour donné. Retourne { mag, termines }. */
export const appliquerChantiers = (mag, totalDays) => {
  const chantiers = mag?.chantiers || [];
  if (!chantiers.length) return { mag, termines: [] };
  const finis = chantiers.filter(c => c.finJour <= totalDays);
  if (!finis.length) return { mag, termines: [] };
  const ameliorations = { ...(mag.ameliorations || {}) };
  const termines = finis.map(c => {
    ameliorations[c.amelioId] = Math.max(ameliorations[c.amelioId] || 0, c.niveauCible);
    const amelio = getAmelioration(c.amelioId);
    return { amelio, niveau: amelio?.niveaux.find(n => n.niveau === c.niveauCible), magNom: mag.nom || null };
  });
  return {
    mag: { ...mag, ameliorations, chantiers: chantiers.filter(c => c.finJour > totalDays) },
    termines,
  };
};

/** Effets cumulés (trafic/conversion) des améliorations sur tout le réseau — télémétrie formateur. */
export const effetsReseauAmeliorations = (retail) => {
  if (!retail?.magasin) return { trafic_pct: 0, conversion_pct: 0 };
  const mags = [retail.magasin, ...(retail.magasins || [])];
  let t = 0;
  let c = 0;
  mags.forEach(m => {
    const fx = computeAmelFx(m);
    t += fx.traficPct;
    c += fx.conversionPct;
  });
  return { trafic_pct: Math.round(t * 10) / 10, conversion_pct: Math.round(c * 10) / 10 };
};

/**
 * ROI de chaque investissement : CA moyen 7 j avant l'achat vs 7 j après la
 * fin des travaux (moment où l'effet démarre). Retourne le journal enrichi.
 */
export const computeRoiInvestissements = (retail, totalDays) => {
  const journal = retail?.journalInvest || [];
  const histo = retail?.stats?.histo || [];
  const caByDay = new Map(histo.map(h => [h.d, h.ca]));
  const avg = (from, to) => {
    let s = 0;
    let n = 0;
    for (let d = from; d <= to; d += 1) {
      if (caByDay.has(d)) { s += caByDay.get(d); n += 1; }
    }
    return n > 0 ? Math.round(s / n) : null;
  };
  return journal.map(e => {
    const finEffet = e.jourEffet ?? e.jour;
    const avant = avg(Math.max(1, e.jour - 6), e.jour);
    const apres = totalDays > finEffet ? avg(finEffet + 1, Math.min(finEffet + 7, totalDays)) : null;
    const complet = totalDays >= finEffet + 7;
    const delta = (avant != null && apres != null) ? apres - avant : null;
    const amortJours = (delta != null && delta > 0) ? Math.ceil(e.cout / delta) : null;
    return { ...e, avant, apres, delta, amortJours, complet, enTravaux: totalDays < finEffet };
  });
};

/** Bilan des investissements d'amélioration sur tout le réseau (Dashboard, comparateur, débrief). */
export const bilanAmeliorations = (retail) => {
  if (!retail?.magasin) return { totalInvest: 0, entretienMois: 0, nbTotal: 0, parMagasin: [] };
  const mags = [
    { ...retail.magasin, nom: retail.magasin.nom || retail.magasin.nomType || 'Magasin principal', principal: true },
    ...(retail.magasins || []),
  ];
  const parMagasin = mags.map((m) => {
    const etat = m.ameliorations || {};
    const coef = coefType(m.typeId);
    let invest = 0;
    let entretienMois = 0;
    const liste = [];
    AMELIORATIONS.forEach((a) => {
      const niv = etat[a.id] || 0;
      if (!niv) return;
      a.niveaux.filter((n) => n.niveau <= niv).forEach((n) => {
        invest += Math.round(n.cout * coef);
        entretienMois += Math.round(n.entretien_mensuel * coef);
      });
      liste.push(`${a.nom}${niv > 1 ? ` niv. ${niv}` : ''}`);
    });
    return { nom: m.nom || m.nomType || 'Magasin', principal: !!m.principal, nb: liste.length, invest, entretienMois, liste };
  });
  return {
    totalInvest: parMagasin.reduce((s, x) => s + x.invest, 0),
    entretienMois: parMagasin.reduce((s, x) => s + x.entretienMois, 0),
    nbTotal: parMagasin.reduce((s, x) => s + x.nb, 0),
    parMagasin,
  };
};
